import { Injectable, inject } from '@angular/core'

import { firstValueFrom } from 'rxjs'

import type { CommentModeTarget } from '@flowx/core-sdk'
import type {
  CommentOwner,
  CommentRun,
} from '@flowx/designer/modules/ai-builder/utils/comment-mode/comment-ui-context.util'
import { RuntimeApiService } from '@flowx/designer/shared/api/runtime.api'
import type { ProcessData } from '@flowx/designer/shared/entities/process-instance.types'

/** Who defines the picked component when it is not the page's resource: `ownerMissing` says it is
 * somebody else's but no owner could be built. */
export type CommentOwnerResolution = { owner: CommentOwner | null; ownerMissing: boolean }

const NO_OWNER: CommentOwnerResolution = { owner: null, ownerMissing: false }
const OWNER_MISSING: CommentOwnerResolution = { owner: null, ownerMissing: true }

/** The embed context of a process's own screen. */
const MAIN_CONTEXT = 'main'

/** How long a comment waits to learn its owner before it is sent without. */
const OWNER_WAIT_MS = 5000

const processOwner = (id: string, instanceUuid: string, nodeFlowxUuid?: string): CommentOwner => ({
  resource_type: 'process',
  id,
  ...(nodeFlowxUuid && { node_id: nodeFlowxUuid }),
  run_id: instanceUuid,
})

/** The flowxUuid of the definition node with that numeric id; the raw response names the list
 * `nodeDefinitions`, the designer's own `nodes` is made from it afterwards. */
const nodeFlowxUuidOf = (read: ProcessData, nodeDefinitionId: number | null): string | undefined =>
  nodeDefinitionId === null
    ? undefined
    : read.definition?.nodeDefinitions?.find(({ id }) => id === nodeDefinitionId)?.flowxUuid

/**
 * Finds the resource that defines a picked component when it is not the resource the page runs:
 * the reusable template it is placed from, or the process whose screen the run shows (a child
 * instance, an embedded subprocess). The SDK says where a node comes from (`CommentModeTarget`);
 * a process owner's definition id and node are not on the node, so they are read from the
 * instance, with the calls that change nothing. Never throws: what cannot be learnt in time is
 * `ownerMissing`.
 */
@Injectable({ providedIn: 'root' })
export class CommentOwnerService {
  readonly #runtime = inject(RuntimeApiService)
  /** Process reads by instance, embed node and embed context. A read that failed or timed out is
   * not kept. */
  readonly #reads = new Map<string, Promise<ProcessData>>()

  /** The owner of `target` on `run`'s page; the target is the one the comment is sent on, not the
   * innermost one picked. */
  ownerOf(target: CommentModeTarget, run: CommentRun): Promise<CommentOwnerResolution> {
    const touched = new Set<string>()

    return new Promise((settle) => {
      const timer = setTimeout(() => {
        touched.forEach((key) => this.#reads.delete(key))
        settle(OWNER_MISSING)
      }, OWNER_WAIT_MS)

      this.#resolve(target, run, touched)
        .catch(() => OWNER_MISSING)
        .then((resolution) => {
          clearTimeout(timer)
          settle(resolution)
        })
    })
  }

  async #resolve(
    { reusableTemplateContext, reusableTemplateDefinitionId, process }: CommentModeTarget,
    run: CommentRun,
    touched: Set<string>
  ): Promise<CommentOwnerResolution> {
    // A template's own resource defines its nodes, wherever the template is placed.
    if (reusableTemplateDefinitionId) {
      return {
        owner: { resource_type: 'reusable-template', id: reusableTemplateDefinitionId },
        ownerMissing: false,
      }
    }

    if (reusableTemplateContext) {
      return OWNER_MISSING
    }

    if (!process) {
      return NO_OWNER
    }

    const { instanceUuid, nodeDefinitionId, context } = process

    if (run.resourceType === 'process' && instanceUuid === run.runId && context === MAIN_CONTEXT) {
      return NO_OWNER
    }

    const owner =
      context === MAIN_CONTEXT
        ? await this.#childInstanceOwner(instanceUuid, nodeDefinitionId, touched)
        : await this.#embeddedOwner(instanceUuid, nodeDefinitionId, context, touched)

    if (!owner) {
      return OWNER_MISSING
    }

    // The page names its own resource already.
    return run.resourceType === 'process' && owner.id === run.resourceDefinitionId
      ? NO_OWNER
      : { owner, ownerMissing: false }
  }

  async #childInstanceOwner(
    instanceUuid: string,
    nodeDefinitionId: number | null,
    touched: Set<string>
  ): Promise<CommentOwner | null> {
    const read = await this.#read(touched, instanceUuid)
    const id = read.instance?.resourceDefinitionId

    return id ? processOwner(id, instanceUuid, nodeFlowxUuidOf(read, nodeDefinitionId)) : null
  }

  /** The subprocess is the one the parent instance lists for that context, read again as that
   * embed, from the parent's context, for its nodes; a failed node read leaves `node_id` out.
   * Never the parent's own definition: a nested embed is not listed on the parent, and has no
   * owner. */
  async #embeddedOwner(
    instanceUuid: string,
    nodeDefinitionId: number | null,
    context: string,
    touched: Set<string>
  ): Promise<CommentOwner | null> {
    const parent = await this.#read(touched, instanceUuid)
    const embed = parent.directEmbeddedSubprocessInstances?.find(
      (entry) => entry.context === context
    )

    if (!embed?.resourceDefinitionId) {
      // The instance may not have started this embed when it was read: read it again next time.
      this.#reads.delete(this.#keyOf(instanceUuid))
      return null
    }

    // The engine looks the embed up by the context it was started from, the parent's.
    const embedded =
      nodeDefinitionId === null
        ? null
        : await this.#read(
            touched,
            instanceUuid,
            embed.embedNodeId,
            parent.instance?.embedContext || MAIN_CONTEXT
          ).catch(() => null)

    return processOwner(
      embed.resourceDefinitionId,
      instanceUuid,
      embedded ? nodeFlowxUuidOf(embedded, nodeDefinitionId) : undefined
    )
  }

  #keyOf(instanceUuid: string, embedNodeId?: number, embedContext?: string): string {
    return `${instanceUuid}|${embedNodeId ?? ''}|${embedContext ?? ''}`
  }

  #read(
    touched: Set<string>,
    instanceUuid: string,
    embedNodeId?: number,
    embedContext?: string
  ): Promise<ProcessData> {
    const key = this.#keyOf(instanceUuid, embedNodeId, embedContext)
    touched.add(key)

    const kept = this.#reads.get(key)

    if (kept) {
      return kept
    }

    const read = firstValueFrom(
      this.#runtime.getProcessInstanceById(instanceUuid, embedNodeId, embedContext)
    ).then(({ resourceContent }) => resourceContent)

    this.#reads.set(key, read)
    read.catch(() => {
      if (this.#reads.get(key) === read) {
        this.#reads.delete(key)
      }
    })

    return read
  }
}
